import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, RotateCcw, X, Send, Loader2 } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { content } from "../../i18n/content";
import { api } from "../../lib/api";
import { isIntlPhone } from "../../lib/validate";
import { loadPublicSettings, fmtIntl } from "../../lib/publicSettings";
import { fmtDate } from "../../lib/api";
import { ChangeAdminEmail, ChangePassword } from "../account/AccountSecurity";
import { TestDataPurge } from "./Cleanup";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const defaults = () => content.fr.figures.items.map(([v, l], i) => {
  const [ve, le] = content.en.figures.items[i];
  return { value_fr: v === "live" ? "" : v, value_en: ve === "live" ? "" : ve, label_fr: l, label_en: le, live: v === "live" };
});

const FiguresEditor = ({ initial }) => {
  const { t } = useLang();
  const x = t.adminX;
  const [items, setItems] = useState(initial || defaults());
  const set = (i, k, v) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  const save = async () => {
    if (items.some((it) => it.label_fr.trim().length < 2 || it.label_en.trim().length < 2 || (!it.live && (!it.value_fr || !it.value_en)))) {
      return toast.error(t.errors.fixErrors);
    }
    try { await api.put("/admin/settings/figures", { items }); toast.success(t.admin.updated); } catch { toast.error(t.errors.generic); }
  };
  const reset = async () => {
    try { await api.delete("/admin/settings/figures"); setItems(defaults()); toast.success(t.admin.updated); } catch { toast.error(t.errors.generic); }
  };

  return (
    <div data-testid="admin-figures-editor">
      <h3 className="sub-title">{x.figuresTitle}</h3>
      <p className="f-note" style={{ margin: "8px 0 10px" }}>{x.figuresDesc}</p>
      {items.map((it, i) => (
        <div className="figure-row" key={i} data-testid={`admin-figure-row-${i}`}>
          {["value_fr", "value_en", "label_fr", "label_en"].map((k) => (
            <div className="f-field" key={k}>
              <label htmlFor={`fig-${i}-${k}`}>{x[{ value_fr: "valueFr", value_en: "valueEn", label_fr: "labelFr", label_en: "labelEn" }[k]]}</label>
              <input id={`fig-${i}-${k}`} className="f-input" value={it[k]} disabled={it.live && k.startsWith("value")} onChange={(e) => set(i, k, e.target.value)} data-testid={`admin-figure-${i}-${k}`} />
            </div>
          ))}
          <label className="check-row" htmlFor={`fig-${i}-live`} style={{ paddingBottom: 12 }}>
            <input type="checkbox" id={`fig-${i}-live`} checked={it.live} onChange={(e) => set(i, "live", e.target.checked)} data-testid={`admin-figure-${i}-live`} /><span>{x.live}</span>
          </label>
          <button type="button" className="btn btn-ghost btn-sm" disabled={items.length <= 1} onClick={() => setItems((a) => a.filter((_, j) => j !== i))} aria-label={`${x.remove} ${i + 1}`}><X aria-hidden="true" /></button>
        </div>
      ))}
      <div className="f-actions">
        <button type="button" className="btn btn-ghost btn-sm" disabled={items.length >= 6} onClick={() => setItems((a) => [...a, { value_fr: "", value_en: "", label_fr: "", label_en: "", live: false }])} data-testid="admin-figure-add-btn"><Plus aria-hidden="true" />{x.addFigure}</button>
        <span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="btn btn-ghost" onClick={reset} data-testid="admin-figures-reset-btn"><RotateCcw aria-hidden="true" />{x.resetFigures}</button>
          <button type="button" className="btn btn-primary" onClick={save} data-testid="admin-figures-save-btn"><Save aria-hidden="true" />{x.saveFigures}</button>
        </span>
      </div>
    </div>
  );
};

const NotificationSettings = ({ initial }) => {
  const { t } = useLang();
  const x = t.adminX;
  const [phone, setPhone] = useState(initial || "");
  const [error, setError] = useState(null);
  const save = async () => {
    if (phone && !isIntlPhone(phone)) return setError(t.errors.intl);
    setError(null);
    try { await api.put("/admin/settings/notifications", { admin_whatsapp: phone ? phone.replace(/[\s().-]/g, "") : null }); toast.success(t.admin.updated); }
    catch { toast.error(t.errors.generic); }
  };
  return (
    <div style={{ marginTop: 36 }} data-testid="admin-notification-settings">
      <h3 className="sub-title">{x.notifTitle}</h3>
      <div className="admin-form-row" style={{ marginTop: 14, gridTemplateColumns: "minmax(0, 420px) auto" }}>
        <div className="f-field">
          <label htmlFor="admin-wa">{x.adminWa}</label>
          <input id="admin-wa" type="tel" className="f-input" placeholder="+33 6 00 00 00 00" value={phone} aria-invalid={error ? "true" : "false"} onChange={(e) => setPhone(e.target.value)} data-testid="admin-whatsapp-input" />
          {error && <span className="f-error" role="alert" data-testid="admin-whatsapp-error">{error}</span>}
        </div>
        <button type="button" className="btn btn-primary" onClick={save} data-testid="admin-whatsapp-save-btn"><Save aria-hidden="true" />{x.saveNotif}</button>
      </div>
    </div>
  );
};

const ContactSettings = ({ initial }) => {
  const { t } = useLang();
  const c = t.contactInfo;
  const [v, setV] = useState({ ...initial, whatsapp: initial.whatsapp || "" });
  const [errors, setErrors] = useState({});
  const save = async () => {
    const errs = {};
    if (!/^[0-9+()\s.-]{6,30}$/.test(v.phone.trim())) errs.phone = t.errors.phone;
    if (!EMAIL_RE.test(v.email.trim())) errs.email = t.errors.email;
    if (v.whatsapp.trim() && !isIntlPhone(v.whatsapp)) errs.whatsapp = t.errors.intl;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const whatsapp = v.whatsapp.trim() ? v.whatsapp.replace(/[\s().-]/g, "") : null;
    try { await api.put("/admin/settings/contact", { phone: v.phone.trim(), email: v.email.trim(), whatsapp }); loadPublicSettings(true); toast.success(t.admin.updated); }
    catch { toast.error(t.errors.generic); }
  };
  return (
    <div className="settings-block" data-testid="admin-contact-settings">
      <h3 className="sub-title">{c.adminTitle}</h3>
      <div className="admin-form-row" style={{ marginTop: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr)) auto" }}>
        {["phone", "email", "whatsapp"].map((k) => (
          <div className="f-field" key={k}>
            <label htmlFor={`contact-${k}`}>{c[k]}</label>
            <input id={`contact-${k}`} className="f-input" value={v[k]} aria-invalid={errors[k] ? "true" : "false"} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`admin-contact-${k}-input`} />
            {errors[k] && <span className="f-error" role="alert" data-testid={`admin-contact-${k}-error`}>{errors[k]}</span>}
            {k === "whatsapp" && <span className="f-note" data-testid="admin-contact-whatsapp-hint">{v.whatsapp.trim() && isIntlPhone(v.whatsapp) ? `${fmtIntl(v.whatsapp)} · ` : ""}{c.whatsappHint}</span>}
          </div>
        ))}
        <button type="button" className="btn btn-primary" onClick={save} data-testid="admin-contact-save-btn"><Save aria-hidden="true" />{c.save}</button>
      </div>
    </div>
  );
};

const fmtDateTime = (iso, lang) => `${fmtDate(iso, lang)} ${new Date(iso).toLocaleTimeString(lang === "en" ? "en-GB" : "fr-FR", { hour: "2-digit", minute: "2-digit" })}`;

const RunInfo = ({ run }) => {
  const { t, lang } = useLang();
  const r = t.report;
  const x = t.reportRun;
  return (
    <>
      {r.last} {run.week} · {fmtDateTime(run.at, lang)} — <b data-testid="admin-report-run-status">{r.runStatus[run.status] || run.status}</b>
      {run.recipient && ` (${run.recipient})`} · <span data-testid="admin-report-run-attempts">{x.attempts(run.attempts)}</span>
      {run.status !== "sent" && run.last_error && <><br /><span data-testid="admin-report-run-error">{x.lastError} {run.last_error}</span></>}
      {run.next_retry_at && <><br /><span data-testid="admin-report-run-next">{x.nextRetry} {fmtDateTime(run.next_retry_at, lang)}</span></>}
      {run.notice_status && <><br />{x.notice} {r.runStatus[run.notice_status] || run.notice_status}</>}
    </>
  );
};

const AlertSettings = ({ initial }) => {
  const { t } = useLang();
  const al = t.alerts;
  const [on, setOn] = useState(initial.pending_expert);
  const toggle = async () => {
    try { await api.put("/admin/settings/alerts", { pending_expert: !on }); setOn(!on); toast.success(t.admin.updated); }
    catch { toast.error(t.errors.generic); }
  };
  return (
    <div className="settings-block" data-testid="admin-alert-settings">
      <h3 className="sub-title">{al.title}</h3>
      <p className="f-note" style={{ margin: "8px 0 0" }}>{al.desc}</p>
      <div className="check-row" style={{ marginTop: 14 }}>
        <button type="button" role="switch" className="toggle" aria-checked={on} aria-label={al.toggle} onClick={toggle} data-testid="admin-pending-alert-toggle" />
        <span>{al.toggle}</span>
      </div>
    </div>
  );
};

const ReportSettings = ({ initial, lastRun }) => {
  const { t } = useLang();
  const r = t.report;
  const [v, setV] = useState({ enabled: initial.enabled, recipient: initial.recipient || "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!EMAIL_RE.test(v.recipient.trim())) return setError(t.errors.email);
    setError(null);
    try { await api.put("/admin/settings/report", { enabled: v.enabled, recipient: v.recipient.trim() }); toast.success(t.admin.updated); }
    catch { toast.error(t.errors.generic); }
  };
  const preview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/report/preview"); toast.success(r.previewSent(data.recipient)); }
    catch { toast.error(r.previewFailed); }
    setBusy(false);
  };
  return (
    <div className="settings-block" data-testid="admin-report-settings">
      <h3 className="sub-title">{r.title}</h3>
      <p className="f-note" style={{ margin: "8px 0 0" }}>{r.desc}</p>
      <label className="check-row" style={{ marginTop: 14 }}>
        <button type="button" role="switch" className="toggle" aria-checked={v.enabled} onClick={() => setV({ ...v, enabled: !v.enabled })} data-testid="admin-report-enabled-toggle" aria-label={r.enabled} />
        <span>{r.enabled}</span>
      </label>
      <div className="report-row">
        <div className="f-field">
          <label htmlFor="report-recipient">{r.recipient}</label>
          <input id="report-recipient" type="email" className="f-input" value={v.recipient} aria-invalid={error ? "true" : "false"} onChange={(e) => setV({ ...v, recipient: e.target.value })} data-testid="admin-report-recipient-input" />
          {error && <span className="f-error" role="alert" data-testid="admin-report-recipient-error">{error}</span>}
        </div>
        <button type="button" className="btn btn-primary" onClick={save} data-testid="admin-report-save-btn"><Save aria-hidden="true" />{r.save}</button>
        <button type="button" className="btn btn-ghost" onClick={preview} disabled={busy} data-testid="admin-report-preview-btn">
          {busy ? <Loader2 className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />}{r.preview}
        </button>
      </div>
      <p className="f-note" style={{ marginTop: 10 }}>{r.previewNote}</p>
      <p className="f-note" data-testid="admin-report-last-run">
        {lastRun ? <RunInfo run={lastRun} /> : r.never}
      </p>
    </div>
  );
};

export const AdminSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  return (
    <div data-testid="admin-settings-panel">
      <FiguresEditor initial={s.figures} />
      <NotificationSettings initial={s.admin_whatsapp} />
      <ContactSettings initial={s.contact} />
      <ReportSettings initial={s.report} lastRun={s.report_last_run} />
      <AlertSettings initial={s.alerts} />
      <div className="settings-block" data-testid="admin-account-settings"><ChangeAdminEmail /></div>
      <div className="settings-block"><ChangePassword /></div>
      <TestDataPurge />
    </div>
  );
};
